import type {
  ChatCompletionResult,
  PlaygroundConversationMessage,
  PlaygroundFunction,
  PlaygroundToolCall,
} from 'services/api/playground.api';
import type { MessageToEval } from 'services/types/conversationEvals';

import type { LaneResult, PlaygroundTurn, TurnRole, TurnToolCall } from '../types';

const VALID_ROLES: TurnRole[] = ['system', 'user', 'assistant', 'tool'];

export const newUid = (): string => crypto.randomUUID();

/** Generate the id the backend requires on a tool call. */
export const newToolCallId = (): string => `call_${crypto.randomUUID().replace(/-/g, '').slice(0, 24)}`;

const coerceRole = (role: unknown): TurnRole => {
  if (typeof role === 'string') {
    if (VALID_ROLES.includes(role as TurnRole)) {
      return role as TurnRole;
    }
    // `function` (legacy) collapses to the tool lane in the editor.
    if (role === 'function') {
      return 'tool';
    }
  }
  return 'assistant';
};

/** Best-effort string for editor display: text stays text, multimodal/array content is stringified. */
const contentToString = (content: unknown): string => {
  if (content == null) {
    return '';
  }
  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    // Join plain text parts; keep anything richer as JSON so it round-trips via raw mode.
    const textParts = content
      .map((part) =>
        part && typeof part === 'object' && 'text' in part ? String((part as { text: unknown }).text) : null
      )
      .filter((t): t is string => t !== null);
    if (textParts.length === content.length) {
      return textParts.join('\n');
    }
    return JSON.stringify(content, null, 2);
  }
  return JSON.stringify(content, null, 2);
};

const toToolCalls = (raw: unknown): TurnToolCall[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  return raw
    .map((tc): TurnToolCall | null => {
      if (!tc || typeof tc !== 'object') {
        return null;
      }
      const fn = (tc as { function?: { name?: unknown; arguments?: unknown } }).function;
      const id = (tc as { id?: unknown }).id;
      return {
        id: typeof id === 'string' && id ? id : newToolCallId(),
        name: typeof fn?.name === 'string' ? fn.name : '',
        arguments: typeof fn?.arguments === 'string' ? fn.arguments : JSON.stringify(fn?.arguments ?? {}, null, 2),
      };
    })
    .filter((tc): tc is TurnToolCall => tc !== null);
};

/**
 * Some stored assistant messages keep their tool calls serialized inside `content` instead of in the
 * `toolCalls` field. Left as text they read as a JSON blob in the editor, and an eval built from such a
 * turn stores the blob as the expected *prose* — so the grader judges text and ignores the call the
 * model actually has to make. Detect that shape and hydrate the real tool calls.
 */
export const toolCallsFromSerializedContent = (content: unknown): TurnToolCall[] | undefined => {
  if (typeof content !== 'string' || !content.trimStart().startsWith('[')) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(content) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return undefined;
    }
    const everyEntryIsAToolCall = parsed.every(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { function?: { name?: unknown } }).function?.name === 'string'
    );
    return everyEntryIsAToolCall ? toToolCalls(parsed) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Map a stored/loaded conversation array into editable turns. Defensive on purpose: the loaded
 * `message.conversation` is typed loosely (the historical `AIResponse[]` bug), so we accept both the
 * conversation shape ({role, content, toolCalls}) and the response shape ({completionMessageContent}).
 */
export const conversationToTurns = (conversation: unknown): PlaygroundTurn[] => {
  if (!Array.isArray(conversation)) {
    return [];
  }
  return conversation.map((raw): PlaygroundTurn => {
    const item = (raw ?? {}) as Record<string, unknown>;
    const hasRole = typeof item.role === 'string';
    const content = hasRole ? item.content : item.completionMessageContent;
    const declaredToolCalls = toToolCalls(item.toolCalls);
    const serializedToolCalls = declaredToolCalls ? undefined : toolCallsFromSerializedContent(content);
    return {
      uid: newUid(),
      role: hasRole ? coerceRole(item.role) : 'assistant',
      content: serializedToolCalls ? '' : contentToString(content),
      toolCalls: declaredToolCalls ?? serializedToolCalls,
      toolCallId: typeof item.toolCallId === 'string' ? item.toolCallId : undefined,
    };
  });
};

/** Map editable turns into the conversation shape the chatCompletion endpoint validates. */
export const turnsToConversation = (turns: PlaygroundTurn[]): PlaygroundConversationMessage[] =>
  turns.map((turn) => {
    const toolCalls: PlaygroundToolCall[] | undefined = turn.toolCalls?.length
      ? turn.toolCalls.map((tc) => ({
          id: tc.id || newToolCallId(),
          type: 'function',
          function: { name: tc.name, arguments: tc.arguments },
        }))
      : undefined;
    return {
      role: turn.role,
      // Assistant messages that only carry tool calls send null content (OpenAI format).
      content: turn.content === '' && toolCalls ? null : turn.content,
      toolCalls,
      toolCallId: turn.toolCallId || undefined,
    };
  });

/** Uid of the turn an eval grades unless the author picks others: the last thing the assistant said. */
export const defaultCheckpointUid = (turns: PlaygroundTurn[]): string | undefined =>
  turns.filter((turn) => turn.role === 'assistant').at(-1)?.uid;

/** Whether a turn will be stored as a tool call, which decides how the backend grades that checkpoint. */
export const turnCallsTools = (turn: PlaygroundTurn): boolean =>
  Boolean(turn.toolCalls?.length || toolCallsFromSerializedContent(turn.content));

/**
 * Map editable turns into the stored-eval shape. `system` turns are dropped (an eval holds only the
 * dialogue — the prompt comes from the business unit at run time). Every turn whose uid carries a
 * description becomes a checkpoint: the runner replays the conversation up to it and grades that turn,
 * so one stored eval can assert several points of the same flow.
 */
export const turnsToEvalMessages = (
  turns: PlaygroundTurn[],
  checkpointDescriptions: Record<string, string> = {}
): MessageToEval[] => {
  const dialogue = turns.filter((turn) => turn.role !== 'system');
  return dialogue.map((turn) => {
    // Hydrate here too, not just in conversationToTurns: turns also reach this mapper straight from a run
    // result or the ground-truth lane. This is the single funnel for creating an eval, so a serialized
    // call can never be stored as expected prose regardless of where the turn was built.
    const sourceToolCalls = turn.toolCalls?.length ? turn.toolCalls : toolCallsFromSerializedContent(turn.content);
    const toolCalls = sourceToolCalls?.length
      ? sourceToolCalls.map((tc) => ({
          id: tc.id || newToolCallId(),
          type: 'function' as const,
          function: { name: tc.name, arguments: tc.arguments },
        }))
      : null;
    return {
      role: turn.role as MessageToEval['role'],
      // Mirrors conversationEvalFromMessage on the backend: a turn that calls tools stores no text. The
      // grader checks the expected text first, so keeping the chit-chat that accompanies a call ("¡Un
      // momento!") would grade that phrase and never compare the tool trajectory.
      text: toolCalls ? null : turn.content || null,
      toolCalls,
      toolCallId: turn.toolCallId || null,
      ...(checkpointDescriptions[turn.uid]?.trim()
        ? { expected: { responseDescription: checkpointDescriptions[turn.uid].trim() } }
        : {}),
    };
  });
};

const SYNTHETIC_TOOL_RESULT = JSON.stringify({ note: 'Herramienta no ejecutada (simulación de playground).' });

/**
 * Make a conversation valid for the LLM API. The provider rejects (400 "Bad Request") any assistant
 * message with tool_calls that is not immediately answered by a tool message for each tool_call id.
 * In the sandbox we never execute tools, so the model can emit a tool call that is then followed by a
 * user turn (live chat) or a mismatched original tool result (replay). This:
 *   - injects a synthetic tool result for any tool_call left unanswered before the next non-tool turn,
 *   - drops orphan tool messages that don't answer an open tool_call.
 * Keeps the editable thread faithful; only the outgoing payload is sanitized.
 */
export const sanitizeConversation = (messages: PlaygroundConversationMessage[]): PlaygroundConversationMessage[] => {
  const out: PlaygroundConversationMessage[] = [];
  let openIds: string[] = [];

  const flushOpen = () => {
    for (const id of openIds) {
      out.push({ role: 'tool', content: SYNTHETIC_TOOL_RESULT, toolCallId: id });
    }
    openIds = [];
  };

  for (const msg of messages) {
    if (msg.role === 'tool') {
      // Keep only tool results that answer a currently-open tool call; drop orphans.
      if (msg.toolCallId && openIds.includes(msg.toolCallId)) {
        out.push(msg);
        openIds = openIds.filter((id) => id !== msg.toolCallId);
      }
      continue;
    }
    // Any unanswered tool calls must be resolved before the next non-tool message.
    if (openIds.length > 0) {
      flushOpen();
    }
    out.push(msg);
    if (msg.role === 'assistant' && msg.toolCalls && msg.toolCalls.length > 0) {
      openIds = msg.toolCalls.map((tc) => tc.id).filter((id): id is string => Boolean(id));
    }
  }
  // Trailing unanswered tool calls (assistant tool_call at the very end of the conversation).
  if (openIds.length > 0) {
    flushOpen();
  }
  return out;
};

export const turnsToRawJson = (turns: PlaygroundTurn[]): string => JSON.stringify(turnsToConversation(turns), null, 2);

/** Parse raw conversation JSON back into turns. Throws on invalid JSON (caller sets the error flag). */
export const rawJsonToTurns = (raw: string): PlaygroundTurn[] => {
  const parsed = JSON.parse(raw) as unknown;
  if (!Array.isArray(parsed)) {
    throw new Error('Conversation must be a JSON array');
  }
  return conversationToTurns(parsed);
};

export const functionsToRawJson = (functions: PlaygroundFunction[]): string => JSON.stringify(functions, null, 2);

/** Parse raw functions JSON, tolerating both `{type,function}` and bare `{name,...}` entries. */
export const rawJsonToFunctions = (raw: string): PlaygroundFunction[] => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return [];
  }
  const parsed = JSON.parse(trimmed) as unknown;
  if (!Array.isArray(parsed)) {
    throw new Error('Functions must be a JSON array');
  }
  return parsed.map((entry) => {
    const obj = entry as Record<string, unknown>;
    if (obj && obj.type === 'function' && obj.function) {
      return obj as unknown as PlaygroundFunction;
    }
    return { type: 'function', function: obj as unknown as PlaygroundFunction['function'] };
  });
};

const resultToolCalls = (toolCalls: ChatCompletionResult['toolCalls']): TurnToolCall[] | undefined => {
  if (!toolCalls || toolCalls.length === 0) {
    return undefined;
  }
  return toolCalls.map((tc) => ({
    id: tc.id || newToolCallId(),
    name: tc.function.name,
    arguments: tc.function.arguments,
  }));
};

/** Convert a /chatCompletion response into the rendered lane result. */
export const chatResultToLaneResult = (result: ChatCompletionResult): LaneResult => ({
  content: result.completionMessageContent ?? '',
  toolCalls: resultToolCalls(result.toolCalls),
  cost: result.cost,
  time: result.time,
});

/** Convert a lane result into an assistant turn (used when appending a run back into the thread). */
export const laneResultToTurn = (result: LaneResult): PlaygroundTurn => ({
  uid: newUid(),
  role: 'assistant',
  content: result.content,
  toolCalls: result.toolCalls,
});
